import React, { useState, useRef, useEffect } from "react";

import SampleTemplates from "../../../../../assets/reports-template.json";
import api from "../../../../../services/api";
import { notify } from "../../../../../services/toast.config";

import { humanReadableDate } from "../../../../../helper/dateTimeHelper";

interface Template {
  id: number;
  name?: string;
  content?: string;
  category?: string;
  icon?: string;
  template_name?: string;
  template_content?: string;
  created_at?: string;
}

interface TemplateSuggestionsProps {
  editor: any;
  onClose: () => void;
  onSelectTemplate: (template: Template) => void;
}

const TemplateSuggestions: React.FC<TemplateSuggestionsProps> = ({
  editor,
  onClose,
  onSelectTemplate,
}) => {
  const signature: string | null = localStorage.getItem("signature");

  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [userTemplates, setUserTemplates] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const templatesPerPage = 3;

  // Main Templates
  const mainTemplates: Template[] = SampleTemplates;

  // Prevent component from closing when clicking inside
  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        containerRef.current.contains(event.target as Node)
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener("mousedown", handleMouseDown, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, []);

  // EFFECTS
  useEffect(() => {
    fetchUserTemplates();
  }, []);

  const fetchUserTemplates = async (): Promise<void> => {
    try {
      const response = await api.post("ers/reports/getAllTemplates", {
        signature: signature,
      });

      const responseData = response.data?.data || [];

      setUserTemplates(responseData);
    } catch (error: any) {
      const errorData = error?.response?.data || error?.data;
      let errorMessage = "Failed to load templates";

      if (Array.isArray(errorData?.message) && errorData?.message[0]?.msg) {
        errorMessage = errorData.message[0].msg;
      } else if (
        typeof errorData?.message === "string" &&
        errorData.message.trim()
      ) {
        errorMessage = errorData.message;
      }

      notify.error(errorMessage);
      setUserTemplates([]);
    }
  };

  const handleTemplateSelect = (
    event: React.MouseEvent,
    template: Template
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const processedContent = template.template_content;
    editor?.commands.setContent(processedContent);
    editor?.commands.focus("end");
    onSelectTemplate(template);
    onClose();
  };

  const handleTemplateDelete = async (template_id: number | any) => {
    try {
      const response = await api.post("ers/reports/deleteTemplate", {
        signature: signature,
        template_id: template_id,
      });

      const responseData = response.data?.data || [];
      setUserTemplates(responseData);
      notify.success(
        response?.data?.message || "Deleted template successfully"
      );
    } catch (error: any) {
      const errorData = error?.response?.data || error?.data;
      let errorMessage = "Failed to delete template";

      if (Array.isArray(errorData?.message) && errorData?.message[0]?.msg) {
        errorMessage = errorData.message[0].msg;
      } else if (
        typeof errorData?.message === "string" &&
        errorData.message.trim()
      ) {
        errorMessage = errorData.message;
      }

      notify.error(errorMessage);
      setUserTemplates([]);
    } finally {
      onClose();
    }
  };

  const handleShowAllClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowAllTemplates(true);
    setCurrentPage(0);
  };

  const handleCloseAll = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowAllTemplates(false);
    setCurrentPage(0);
  };

  const totalPages = Math.ceil(userTemplates.length / templatesPerPage);
  const currentTemplates = userTemplates.slice(
    currentPage * templatesPerPage,
    (currentPage + 1) * templatesPerPage
  );

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  if (showAllTemplates) {
    return (
      <div
        className="template-suggestions-overlay"
        ref={containerRef}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}>
        <div className="template-overlay-header">
          <h5 className="mb-0">
            <i className="ti ti-template me-2"></i>
            All Templates
          </h5>
          <button
            className="btn btn-sm btn-outline-info"
            onClick={handleCloseAll}>
            <i className="ti ti-x"></i>
          </button>
        </div>

        <div className="template-overlay-grid">
          {currentTemplates.length > 0 ? (
            currentTemplates.map((template) => (
              <div
                key={template.id}
                className="template-overlay-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}>
                <div className="template-overlay-icon">
                  <i className="ti ti-template"></i>
                </div>
                <div
                  className="template-overlay-content"
                  onClick={(e) => handleTemplateSelect(e, template)}>
                  <div className="template-overlay-name">
                    {" "}
                    <h5>{template.template_name}</h5>
                  </div>
                  <span className="text-muted">
                    {humanReadableDate(template.created_at)}
                  </span>
                </div>
                <div className="template-overlay">
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleTemplateDelete(template.id)}>
                    <i className="ti ti-trash-x me-1"></i>
                    Delete
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col ">
              <div className="text-center py-4">
                <i className="ti ti-template text-primary fs-40"></i>
                <h5 className="mt-3 text-primary">No Templates Yet</h5>
              </div>

              <div className="alert alert-info d-flex aligin-items-center justify-content-center">
                <i className="ti ti-info-circle me-2"></i>
                Create a new template or choose from your recent reports.
              </div>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="template-overlay-pagination">
            <button
              className="btn btn-sm btn-outline-info"
              onClick={handlePrevPage}
              disabled={currentPage === 0}>
              <i className="ti ti-chevron-left me-1"></i>
              Previous
            </button>
            <span className="pagination-info">
              {currentPage + 1} of {totalPages}
            </span>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}>
              Next
              <i className="ms-1 ti ti-chevron-right"></i>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="template-suggestions-main"
      ref={containerRef}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}>
      {/* Quick Template Row */}
      <div className="template-quick-row">
        {mainTemplates.map((template) => (
          <div
            key={template.id}
            className="template-quick-box"
            onClick={(e) => handleTemplateSelect(e, template)}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}>
            <div className="template-quick-icon">
              <i className={template.icon}></i>
            </div>
            <div className="template-quick-text">{template.name}</div>
          </div>
        ))}

        {/* Show All Templates Box */}
        <div
          className="template-quick-box template-show-all"
          onClick={handleShowAllClick}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}>
          <div className="template-quick-icon">
            <i className="ti ti-apps"></i>
          </div>
          <div className="template-quick-text">All Templates</div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSuggestions;
